'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CurrencyInput } from './CurrencyInput'

interface EditSpendLimitPopoverProps {
  currentCapCents: number | null
  onSaved: () => void
  trigger: React.ReactNode
}

export function EditSpendLimitPopover({
  currentCapCents,
  onSaved,
  trigger,
}: EditSpendLimitPopoverProps) {
  const [open, setOpen] = useState(false)
  const [valueCents, setValueCents] = useState(0)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setValueCents(currentCapCents ?? 0)
  }, [currentCapCents, open])

  const handleSave = async () => {
    if (!Number.isFinite(valueCents) || valueCents < 0) {
      toast.error('Enter a valid spend limit.')
      return
    }

    setSaving(true)
    try {
      const res = await fetch('/api/budget/cap', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthly_cap_cents: valueCents }),
      })
      if (!res.ok) {
        const payload = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(payload.error || 'Failed to save spend limit')
      }
      onSaved()
      setOpen(false)
      toast.success('Spend limit updated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save spend limit')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 border-atrium-border bg-white p-4 font-sans dark:bg-atrium-inner"
      >
        <div>
          <h3 className="text-lg font-medium text-atrium-black">Edit spend limit</h3>
          <p className="mt-1 text-sm text-atrium-muted">
            Set a monthly ad spend limit for this workspace.
          </p>
        </div>

        <div className="mt-4">
          <label htmlFor="spend-limit-usd" className="text-sm font-medium text-atrium-black">
            Monthly limit (USD)
          </label>
          <CurrencyInput
            id="spend-limit-usd"
            valueCents={valueCents}
            onChangeCents={setValueCents}
            minCents={0}
            placeholder="5000"
            disabled={saving}
          />
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
